import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ROLE_LABEL, formatCAD, formatDate } from "@/lib/utils-cart";
import { toast } from "sonner";

export function AppGuides() {
  const { user, primaryRole } = useAuth();
  const [role, setRole] = useState(primaryRole());
  const [guides, setGuides] = useState([]);
  useEffect(() => { api.get(`/guides?role=${role}`).then((r) => setGuides(r.data)); }, [role]);

  const roles = (user?.roles || []).filter((r) => r !== "customer" && r !== "super_admin");
  return (
    <DashboardLayout title="Role Guides" subtitle="Step-by-step playbooks for your role.">
      <div className="flex flex-wrap gap-2 mb-6">
        {(roles.length ? roles : ["supplier", "merchant", "affiliate"]).map((r) => (
          <button key={r} onClick={() => setRole(r)} className={`v-btn-outline ${role === r ? "border-primary bg-primary text-white" : ""}`} data-testid={`app-guides-role-${r}`}>{ROLE_LABEL[r]}</button>
        ))}
      </div>
      <div className="space-y-3 max-w-3xl">
        {guides.map((g, i) => (
          <div key={g.id} className="v-card">
            <div className="font-mono text-xs text-muted-foreground">Chapter {i + 1}</div>
            <h2 className="font-display text-xl font-bold text-primary mt-1">{g.title}</h2>
            <p className="mt-2 text-muted-foreground leading-relaxed">{g.body}</p>
          </div>
        ))}
        {guides.length === 0 && <div className="v-card text-center text-muted-foreground py-10">No guides for this role yet.</div>}
      </div>
    </DashboardLayout>
  );
}

export function AppHelp() {
  const [faqs, setFaqs] = useState([]);
  useEffect(() => { api.get("/faqs").then((r) => setFaqs(r.data)); }, []);

  return (
    <DashboardLayout title="Help Center" subtitle="Quick answers. If you need more, email hello@vendora.ca">
      <div className="space-y-2 max-w-3xl">
        {faqs.map((f) => (
          <div key={f.id} className="v-card">
            <div className="font-display font-semibold text-primary">{f.question}</div>
            <p className="mt-2 text-sm text-muted-foreground">{f.answer}</p>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}

// Customer/merchant view: their own service requests + responses
export function MyLeads() {
  const [leads, setLeads] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: "accounting", description: "", budget: "", timeline: "" });
  const load = () => api.get("/leads").then((r) => setLeads(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leads", { ...form, budget: form.budget ? Number(form.budget) : undefined });
      toast.success("Lead posted");
      setOpen(false); setForm({ category: "accounting", description: "", budget: "", timeline: "" }); load();
    } catch (e) { toast.error("Failed"); }
  };

  const cats = ["accounting","payroll","marketing","legal","insurance","cybersecurity","it_services","erp_consulting","hr_services"];

  return (
    <DashboardLayout title="Request Services" subtitle="Post a lead and let providers compete to win your business."
      actions={<button onClick={() => setOpen(!open)} className="v-btn-primary" data-testid="my-leads-add">+ New request</button>}
    >
      {open && (
        <form onSubmit={create} className="v-card mb-4 space-y-3">
          <select className="v-input" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} data-testid="my-lead-category">
            {cats.map((c) => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
          </select>
          <textarea rows={3} className="v-input" placeholder="Describe what you need" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} required data-testid="my-lead-description" />
          <div className="grid sm:grid-cols-2 gap-2">
            <input type="number" className="v-input" placeholder="Budget CAD" value={form.budget} onChange={(e) => setForm({...form, budget: e.target.value})} data-testid="my-lead-budget" />
            <input className="v-input" placeholder="Timeline" value={form.timeline} onChange={(e) => setForm({...form, timeline: e.target.value})} data-testid="my-lead-timeline" />
          </div>
          <div className="flex gap-2"><button className="v-btn-primary" data-testid="my-lead-save">Post</button><button type="button" onClick={() => setOpen(false)} className="v-btn-outline">Cancel</button></div>
        </form>
      )}

      <div className="grid md:grid-cols-2 gap-3">
        {leads.map((l) => (
          <div key={l.id} className="v-card">
            <div className="flex items-center justify-between">
              <div className="label-eyebrow text-secondary">{l.category?.replace("_", " ")}</div>
              <span className="text-xs text-muted-foreground font-mono">{formatDate(l.created_at)}</span>
            </div>
            <p className="mt-3 text-sm">{l.description}</p>
            <div className="mt-3 text-xs text-muted-foreground">
              {l.budget && <span>Budget {formatCAD(l.budget)} · </span>}
              <span>{l.responses?.length || 0} quotes received</span>
            </div>
            {l.responses?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-border space-y-2">
                {l.responses.map((r) => (
                  <div key={r.id} className="text-sm">
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-primary">{r.provider_name}</div>
                      <div className="font-mono">{formatCAD(r.quote)}</div>
                    </div>
                    <div className="text-xs text-muted-foreground">{r.message}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        {leads.length === 0 && <div className="col-span-2 v-card text-center text-muted-foreground py-10">No requests yet. Click "New request" to post one.</div>}
      </div>
    </DashboardLayout>
  );
}
